<template>
    <app-layout :showTrialPeriodBar="false" :isPageLoading="isLoading" :isGlobalErrorHandlerUsed="true">
        <common-page-plain>
            <div class="mcard-request-root">
                <div v-if="!isLoading && !isValid" class="mcard-request-message">
                    <h2>{{ $t('pages.public.mcard_request.invalid_heading') }}</h2>
                    <p>{{ $t('pages.public.mcard_request.invalid_message') }}</p>
                </div>

                <div v-else-if="!isLoading && isSent" class="mcard-request-message">
                    <h2>{{ $t('pages.public.mcard_request.sent_heading') }}</h2>
                    <p>{{ $t('pages.public.mcard_request.sent_message') }}</p>
                    <p>{{ $t('pages.public.mcard_request.sent_account_hint') }}</p>
                    <common-buttons align="center">
                        <common-button
                            :label="$t('pages.public.mcard_request.log_in')"
                            type="secondary"
                            @click="goToLogin"
                        ></common-button>
                    </common-buttons>
                </div>

                <div v-else-if="!isLoading" class="mcard-request-form">
                    <h2>{{ $t('pages.public.mcard_request.heading') }}</h2>
                    <p>{{ $t('pages.public.mcard_request.intro') }}</p>

                    <input
                        v-model.trim="npi"
                        type="text"
                        inputmode="numeric"
                        maxlength="10"
                        class="field"
                        :placeholder="$t('pages.public.mcard_request.npi')"
                    />
                    <input
                        v-model.trim="name"
                        type="text"
                        maxlength="120"
                        class="field"
                        :placeholder="$t('pages.public.mcard_request.name')"
                    />
                    <input
                        v-model.trim="practice"
                        type="text"
                        maxlength="160"
                        class="field"
                        :placeholder="$t('pages.public.mcard_request.practice')"
                    />
                    <input
                        v-model.trim="contact"
                        type="text"
                        maxlength="160"
                        class="field"
                        :placeholder="$t('pages.public.mcard_request.contact')"
                        @keydown.enter.prevent="submit"
                    />

                    <p v-if="errorMessage" class="error">{{ errorMessage }}</p>

                    <common-buttons align="center">
                        <common-button
                            :label="$t('pages.public.mcard_request.submit')"
                            type="primary"
                            :isProcessing="isSubmitting"
                            :isDisabled="!canSubmit || isSubmitting"
                            @click="submit"
                        ></common-button>
                        <common-button
                            :label="$t('pages.public.mcard_request.log_in')"
                            type="secondary"
                            @click="goToLogin"
                        ></common-button>
                    </common-buttons>
                </div>
            </div>
        </common-page-plain>
    </app-layout>
</template>

<script>
    import AppLayout from 'components/layouts/AppLayout'
    import McardRequestsAPI from 'api/McardRequestsAPI'
    import { isValidNpi } from '~/utils/npi'

    export default {
        components: {
            AppLayout,
        },

        data() {
            return {
                isLoading: true,
                isValid: false,
                isSent: false,
                isSubmitting: false,
                errorMessage: '',
                npi: '',
                name: '',
                practice: '',
                contact: '',
            }
        },

        computed: {
            token() {
                return this.$route.params.token
            },

            canSubmit() {
                return isValidNpi(this.npi) && this.name.length > 0
            },
        },

        methods: {
            // Come back to this page after signing in, so the provider can still send or follow up the request.
            goToLogin() {
                this.$router.push({ name: 'auth.login', query: { redirect: this.$route.fullPath } })
            },

            submit() {
                if (!this.canSubmit || this.isSubmitting) return

                this.isSubmitting = true
                this.errorMessage = ''

                return McardRequestsAPI.create(this.token, {
                    npi: this.npi,
                    name: this.name,
                    practice: this.practice || undefined,
                    contact: this.contact || undefined,
                })
                    .then(() => {
                        this.isSent = true
                    })
                    .catch((err) => {
                        const subCode = err?.response?.data?.subCode
                        if (subCode === 'token.invalid') {
                            this.isValid = false
                        } else if (subCode === 'npi.invalid') {
                            this.errorMessage = this.$t('pages.public.mcard_request.errors.npi_invalid')
                        } else if (subCode === 'request.rate_limited') {
                            this.errorMessage = this.$t('pages.public.mcard_request.errors.rate_limited')
                        } else {
                            this.errorMessage = this.$t('pages.public.mcard_request.errors.failed')
                        }
                    })
                    .finally(() => {
                        this.isSubmitting = false
                    })
            },
        },

        mounted() {
            // An unknown and a revoked token both answer 404, and the page shows the same message for both.
            McardRequestsAPI.getStatus(this.token)
                .then((res) => {
                    this.isValid = !!res.valid
                })
                .catch(() => {
                    this.isValid = false
                })
                .finally(() => {
                    this.isLoading = false
                })
        },
    }
</script>

<style scoped lang="scss">
    .mcard-request-root {
        width: 100%;
    }

    .mcard-request-message {
        text-align: center;
        padding: 40px 20px;

        p {
            margin-bottom: 24px;
        }
    }

    .mcard-request-form {
        max-width: 420px;
        margin: 0 auto;
        padding: 24px 20px;

        h2 {
            text-align: center;
        }

        p {
            margin-bottom: 20px;
            text-align: center;
        }
    }

    .field {
        width: 100%;
        padding: 10px 12px;
        border: 1px solid #d1d5db;
        border-radius: 6px;
        font-size: 15px;
        margin-bottom: 12px;
        box-sizing: border-box;

        &:focus {
            outline: none;
            border-color: #2e8b57;
        }
    }

    .error {
        color: #c62828;
        margin-bottom: 12px;
        font-size: 13px;
    }
</style>
